import { useState } from 'react';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';

import AlertBar from '@/design_system/AlertBar';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { TranslatableInputText } from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import IconCheck from '@/icons/Check.svg';
import IconChevron from '@/icons/Chevron.svg';
import IconCross from '@/icons/Cross.svg';
import IconEdit from '@/icons/Edit.svg';
import IconTrash from '@/icons/Trash.svg';
import { useUpdateWorkflowConfig, Workflow } from '@/models/workflow';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';
import { trimI18nWording } from '@/utils/trimI18nWording';

const BANNER_TYPES = [
  {
    id: 'info',
    text: msg({
      id: 'settings.workflows.general.new-request-info-banners.type.info',
      message: 'Info',
    }),
  },
  {
    id: 'success',
    text: msg({
      id: 'settings.workflows.general.new-request-info-banners.type.success',
      message: 'Success',
    }),
  },
  {
    id: 'warning',
    text: msg({
      id: 'settings.workflows.general.new-request-info-banners.type.warning',
      message: 'Warning',
    }),
  },
  {
    id: 'error',
    text: msg({
      id: 'settings.workflows.general.new-request-info-banners.type.error',
      message: 'Error',
    }),
  },
];

const TitleAndDescription = () => (
  <Stack>
    <Title as="h2">
      <Trans id="settings.workflows.general.new-request-info-banners.title">
        New request info banners
      </Trans>
    </Title>
    <Paragraph size="200" color="secondary">
      <Trans id="settings.workflows.general.new-request-info-banners.description">
        These banners are displayed at the top of request creation form.
      </Trans>
    </Paragraph>
  </Stack>
);

export const NewRequestInfoBanners = ({ workflow }: { workflow: Workflow }) => {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <Box>
      {isEditing ? (
        <NewRequestInfoBannersEdit workflow={workflow} onSaveOrCancel={() => setIsEditing(false)} />
      ) : (
        <NewRequestInfoBannersView workflow={workflow} onEdit={() => setIsEditing(true)} />
      )}
    </Box>
  );
};

const NewRequestInfoBannersView = ({
  workflow,
  onEdit,
}: {
  workflow: Workflow;
  onEdit: () => void;
}) => {
  const { _db } = useDatabaseTranslation();

  const [showBanners, setShowBanners] = useState(false);

  return (
    <Stack gap="1rem">
      <Stack row gap="1rem" justifyContent="space-between" alignItems="flex-start">
        <TitleAndDescription />
        <Button iconOnly variant="secondary" size="medium" onPress={onEdit}>
          <IconEdit />
        </Button>
      </Stack>

      {workflow.config.newRequestInfoBanners?.length ? (
        <div>
          <Button variant="tertiary" size="small" onPress={() => setShowBanners(!showBanners)}>
            <IconChevron down={!showBanners} up={showBanners} />
            {showBanners ? (
              <Trans id="settings.workflows.general.new-request-info-banners.hide-banners">
                Hide {workflow.config.newRequestInfoBanners?.length ?? 0} banners
              </Trans>
            ) : (
              <Trans id="settings.workflows.general.new-request-info-banners.show-banners">
                Show {workflow.config.newRequestInfoBanners?.length ?? 0} banners
              </Trans>
            )}
          </Button>
        </div>
      ) : (
        <Paragraph size="100">
          <Trans id="settings.workflows.general.new-request-info-banners.no-banners">
            No banners have been added yet.
          </Trans>
        </Paragraph>
      )}

      {showBanners && (
        <Stack gap="0.5rem">
          {workflow.config.newRequestInfoBanners?.map((newRequestInfoBanner, key) => (
            <AlertBar
              key={key}
              type={newRequestInfoBanner.type}
              title={_db(newRequestInfoBanner.message)}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const NewRequestInfoBannersEdit = ({
  workflow,
  onSaveOrCancel,
}: {
  workflow: Workflow;
  onSaveOrCancel: () => void;
}) => {
  const { t, i18n } = useLingui();
  const showToast = useShowToast();
  const { mutateAsync: updateWorkflowConfig, isPending } = useUpdateWorkflowConfig(workflow.id);

  const [banners, setBanners] = useState<
    {
      type: 'info' | 'success' | 'warning' | 'error';
      message: I18nWording;
    }[]
  >(workflow.config.newRequestInfoBanners || []);

  const handleSave = async () => {
    try {
      await updateWorkflowConfig({
        newRequestInfoBanners: banners.map((banner) => ({
          ...banner,
          message: trimI18nWording(banner.message),
        })),
      });
      onSaveOrCancel();
      showToast({
        text: (
          <Trans id="settings.workflows.general.new-request-info-banners.save.success">
            Banners saved successfully.
          </Trans>
        ),
        type: 'success',
      });
    } catch {
      showToast({
        text: (
          <Trans id="settings.workflows.general.new-request-info-banners.save.error">
            Could not save banners. Please try again.
          </Trans>
        ),
        type: 'error',
      });
    }
  };

  return (
    <>
      <Stack row gap="1rem" justifyContent="space-between" alignItems="flex-start">
        <TitleAndDescription />
        <Stack row gap="0.5rem">
          <Button
            iconOnly
            variant="primary"
            size="medium"
            onPress={handleSave}
            isLoading={isPending}
            disabled={isPending}
          >
            <IconCheck />
          </Button>
          <Button
            iconOnly
            variant="secondary-danger"
            size="medium"
            onPress={onSaveOrCancel}
            disabled={isPending}
          >
            <IconCross />
          </Button>
        </Stack>
      </Stack>
      <Stack gap="0.5rem" style={{ marginTop: '1rem' }}>
        {banners.map((banner, index) => (
          <Stack row gap="0.5rem" key={index}>
            <InputSelect
              aria-label={t({
                id: 'settings.workflows.general.new-request-info-banners.type',
                message: 'Type',
              })}
              options={BANNER_TYPES}
              value={BANNER_TYPES.find((option) => option.id === banner.type)}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => i18n._(option.text)}
              style={{ width: '100px' }}
              size="small"
              isDisabled={isPending}
              onChange={(value) =>
                setBanners(
                  banners.map((banner, i) =>
                    i === index
                      ? { ...banner, type: value?.id as 'info' | 'success' | 'warning' | 'error' }
                      : banner
                  )
                )
              }
            />
            <TranslatableInputText
              aria-label={t({
                id: 'settings.workflows.general.new-request-info-banners.message',
                message: 'Message',
              })}
              value={banner.message}
              onChange={(value) =>
                setBanners(
                  banners.map((banner, i) => (i === index ? { ...banner, message: value } : banner))
                )
              }
              wrapperStyle={{ flex: 1 }}
              placeholder={t({
                id: 'settings.workflows.general.new-request-info-banners.message.placeholder',
                message: 'Enter your message',
              })}
              size="small"
              isDisabled={isPending}
            />
            <Button
              iconOnly
              variant="secondary-danger"
              size="medium"
              onPress={() => setBanners(banners.filter((_, i) => i !== index))}
              disabled={isPending}
            >
              <IconTrash />
            </Button>
          </Stack>
        ))}
        <Stack row justifyContent="flex-end">
          <Button
            variant="secondary"
            size="small"
            onPress={() => setBanners([...banners, { type: 'info', message: { fr: '', en: '' } }])}
            disabled={isPending}
          >
            <IconAdd />
            <Trans id="settings.workflows.general.new-request-info-banners.add-banner">
              Add banner
            </Trans>
          </Button>
        </Stack>
      </Stack>
    </>
  );
};
